import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import SCurrencyDisplay from 'shared/components/CurrencyDisplay';
import { TYPEOF } from 'shared/constants';
import generateRender from 'shared/utils/generateRender';
import { useTheme } from 'styled-components';
import Bar from '../Bar/Bar';
import ImageBW from '../ImageBoundsWrapper';
import Tooltip from '../Tooltip';
import { CoinsGlowAnimation, CurrencyImageWrapper } from './styledComponents';
import styled from 'styled-components';
import useIconTooltipPositionCalc from './hooks/useIconTooltipPositionCalc';
import { isMobile } from 'react-device-detect';

const CurrencyDisplay = memo(function CurrencyDisplay(props) {
  const {
    value,
    imageOnly,
    onClick,
    navigation,
    source,
    iconType,
    isCoinsGlowAnimationEnabled,
    isCoinsGlowAnimationStopped,
    ibw,
    manualAssetScale,
    imgStyle,
    style,
    additionalWrapperClassNames,
    tooltipType,
    currencyType,
    changeTooltipType,
    showPiggyBankWidget,
    ...rest
  } = props;

  const theme = useTheme();

  const mountedRef = useRef(false);
  const [displayValue, setDisplayValue] = useState(value);
  const { iconImageRef, iconTooltipPosition } = useIconTooltipPositionCalc({
    imageOnly,
    tooltipWidth: props.containerWidth,
  });

  useEffect(() => {
    mountedRef.current = true;
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    setDisplayValue(value);
  }, [value]);

  const handleHover = useCallback(() => {
    changeTooltipType && changeTooltipType(currencyType);
  }, [changeTooltipType, currencyType]);

  const toggleTooltip = useCallback(() => {
    if (!changeTooltipType) return;
    changeTooltipType(tooltipType === currencyType ? null : currencyType);
  }, [changeTooltipType, tooltipType, currencyType]);

  const onClickHandlerStable = useCallback(
    (handler, needHoverLocal) => {
      if (typeof handler === TYPEOF.FUNCTION) {
        return () => (!needHoverLocal ? handler(currencyType) : null);
      }
      return handler;
    },
    [currencyType]
  );

  return (
    <SCurrencyDisplay
      {...rest}
      navigation={navigation}
      value={displayValue}
      imageOnly={imageOnly}
      onClick={onClick}
      toggleTooltip={toggleTooltip}
      render={args => {
        const { displayText, rawValue, truncated, onClickHandler, containerHeight, containerWidth } = args;

        const needHover = truncated || imageOnly;

        const tooltipStyle = {
          height: containerHeight,
          width: containerWidth,
          left: imageOnly ? iconTooltipPosition.left : 'unset',
        };

        const labelStyle = iconType === 'coins' ? theme.CurrencyDisplay.CoinsLabel : theme.CurrencyDisplay.LoyaltyLabel;
        const iconStyle = theme.CurrencyDisplay.Icons[iconType];

        const barAnimation =
          !isCoinsGlowAnimationStopped && isCoinsGlowAnimationEnabled ? CoinsGlowAnimation : undefined;

        return (
          <Wrapper
            id="CurrencyDisplay"
            className={`CurrencyDisplayWrapper ${additionalWrapperClassNames || ''}`}
            $isClickable={!!onClick}
            $showPiggyBankWidget={showPiggyBankWidget}
            $iconType={iconType}
            style={style}
            onMouseEnter={!isMobile && needHover ? handleHover : undefined}
            onClick={isMobile && needHover ? handleHover : onClickHandlerStable(onClickHandler, needHover)}
          >
            <CurrencyImageWrapper id="CurrencyBar" className="CurrencyIconWrapper" imageOnly={imageOnly}>
              {!imageOnly ? (
                <Bar
                  barIcon={source}
                  label={displayText}
                  labelStyle={labelStyle}
                  iconStyle={iconStyle}
                  animation={barAnimation}
                  shineEffect={shineEffectStatus}
                />
              ) : ibw ? (
                <ImageBW
                  customRef={iconImageRef}
                  source={source}
                  ibw={ibw}
                  manualAssetScale={manualAssetScale || 3}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: -2,
                    zIndex: 1,
                    height: 39,
                    width: 32,
                    objectFit: 'contain',
                    ...imgStyle,
                  }}
                />
              ) : (
                <CurrencyImg src={source} $customStyle={imgStyle} alt="" />
              )}

              <Tooltip
                source={source}
                ibw={ibw}
                displayText={rawValue}
                iconType={iconType}
                visible={tooltipType && tooltipType === currencyType}
                actionHandler={onClick}
                style={tooltipStyle}
              />
            </CurrencyImageWrapper>
          </Wrapper>
        );
      }}
    />
  );
});

CurrencyDisplay.displayName = 'CurrencyDisplay';

export default CurrencyDisplay;

const shineEffectStatus = generateRender({
  default: false,
});

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: ${({ $iconType, $showPiggyBankWidget }) =>
    $iconType === 'coins' && $showPiggyBankWidget ? 'auto' : '1rem'};
  max-width: ${({ $iconType }) => ($iconType === 'coins' ? 'unset' : '230px')};
  ${({ $isClickable }) => ($isClickable ? 'cursor: pointer;' : '')}
`;

const CurrencyImg = styled.img`
  position: absolute;
  left: 0;
  top: -2px;
  z-index: 1;
  height: 39px;
  width: 32px;
  object-fit: contain;
  ${({ $customStyle }) => $customStyle};
`;

